import { useId, type HTMLAttributes } from "react";
import { Controller, type FieldValues } from "react-hook-form";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { FieldShell } from "./FieldShell";
import { useFieldControl, type BaseFieldProps } from "./fieldTypes";

type TextFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  placeholder?: string;
  type?: "text" | "email" | "tel" | "number" | "password" | "url";
  readOnly?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  inputClassName?: string;
};

/** Single-line text bound to a string field. */
export function TextField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  placeholder,
  type = "text",
  readOnly,
  autoFocus,
  autoComplete,
  inputMode,
  inputClassName
}: TextFieldProps<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell
          id={id}
          label={label}
          required={required}
          description={description}
          error={fieldState.error}
          disabled={disabled}
          className={className}
        >
          <Input
            id={id}
            ref={field.ref}
            name={field.name}
            type={type}
            value={field.value ?? ""}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            inputMode={inputMode}
            aria-invalid={fieldState.invalid || undefined}
            className={cn(readOnly && "select-text", inputClassName)}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
          />
        </FieldShell>
      )}
    />
  );
}

type TextareaFieldProps<T extends FieldValues> = BaseFieldProps<T> & {
  placeholder?: string;
  rows?: number;
};

/** Multi-line text bound to a string field. */
export function TextareaField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  placeholder,
  rows = 3
}: TextareaFieldProps<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell
          id={id}
          label={label}
          required={required}
          description={description}
          error={fieldState.error}
          disabled={disabled}
          className={className}
        >
          <Textarea
            id={id}
            ref={field.ref}
            name={field.name}
            value={field.value ?? ""}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            aria-invalid={fieldState.invalid || undefined}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
          />
        </FieldShell>
      )}
    />
  );
}
